import { getRoundedResolution } from '../utilities/getFramingChartElementResolutions';
import { v4 as uuidv4 } from 'uuid';
import { Resolution } from 'src/domains/calculators/utilities/types';
import { isEqual } from 'lodash';
import { getProtectionResolution } from '../utilities/getProtectionResolution';
import { FramingChartProps } from './types';
import { getSimplifiedAspectRatio } from '../utilities/getSimplifiedAspectRatio';

export type GenerateFDLProps = Pick<
  FramingChartProps,
  | 'aspectRatio'
  | 'canvasResolution'
  | 'chartText'
  | 'effectiveCanvas'
  | 'effectiveCanvasCoordinates'
  | 'framingDecisionCoordinates'
  | 'framingDecisionProtectionCoordinates'
  | 'framingDecisionProtection'
  | 'framingDecision'
  | 'framingIntentLabel'
  | 'lensSqueezeFactor'
  | 'protection'
>;

export function generateFDL({
  aspectRatio,
  canvasResolution,
  chartText,
  effectiveCanvas,
  effectiveCanvasCoordinates,
  framingDecisionCoordinates,
  framingDecisionProtectionCoordinates,
  framingDecisionProtection,
  framingDecision,
  framingIntentLabel,
  lensSqueezeFactor,
  protection,
}: GenerateFDLProps) {
  const fdl_creator = 'Netflix Production Technology Tools';
  const canvasId = '1';
  const framingIntentId = '1';

  const rawAspectRatioResolution = getProtectionResolution({
    aspectRatio,
    containerResolution: effectiveCanvas || canvasResolution,
    squeeze: 1, // FDLs should always use desqueezed values
  });

  return {
    uuid: uuidv4(),
    version: {
      major: 2,
      minor: 0,
    },
    fdl_creator,
    default_framing_intent: framingIntentId,
    framing_intents: [
      {
        label: framingIntentLabel,
        id: framingIntentId,
        aspect_ratio: getFDLAspectRatioResolution(
          rawAspectRatioResolution
        ),
        protection,
      },
    ],
    contexts: [
      {
        label: chartText.cameraName,
        context_creator: fdl_creator,
        canvases: [
          {
            label: chartText.sensorModeName,
            id: canvasId,
            source_canvas_id: canvasId,
            dimensions: {
              width: canvasResolution.width,
              height: canvasResolution.height,
            },
            ...(effectiveCanvas ? { effective_dimensions: effectiveCanvas } : {}),
            ...(effectiveCanvasCoordinates
              ? { effective_anchor_point: effectiveCanvasCoordinates }
              : {}),
            anamorphic_squeeze: lensSqueezeFactor,
            framing_decisions: [
              {
                label: framingIntentLabel,
                id: `${canvasId}-${framingIntentId}`,
                framing_intent_id: framingIntentId,
                dimensions: framingDecision,
                anchor_point: framingDecisionCoordinates,
                ...(!isEqual(
                  framingDecisionProtection,
                  framingDecision
                )
                  ? {
                      protection_dimensions:
                        framingDecisionProtection,
                    }
                  : {}),
                ...(!isEqual(
                  framingDecisionProtectionCoordinates,
                  framingDecisionCoordinates
                )
                  ? {
                      protection_anchor_point:
                        framingDecisionProtectionCoordinates,
                    }
                  : {}),
              },
            ],
          },
        ],
      },
    ],
  };
}

function getFDLAspectRatioResolution(
  desqueezedAspectRatioResolution: Resolution
): Resolution {
  if (
    Number.isInteger(desqueezedAspectRatioResolution.width) &&
    Number.isInteger(desqueezedAspectRatioResolution.height)
  ) {
    return getSimplifiedAspectRatio(desqueezedAspectRatioResolution);
  }

  return getSimplifiedAspectRatio(
    getRoundedResolution(desqueezedAspectRatioResolution)
  );
}
